import { Blocks } from "@/components/cms/Blocks";
import { PageSections, type SectionProps, type SectionRenderers } from "@/components/cms/PageSections";
import { linkSlot } from "@/components/cms/SectionRenderer";
import { ArrowRight, ButtonLink } from "@/components/ui/Button";
import { DocumentLibrary } from "@/components/ui/DocumentLibrary";
import { PageHeader } from "@/components/ui/PageHeader";
import { PartnerStrip } from "@/components/ui/PartnerStrip";
import { Reveal } from "@/components/ui/Reveal";
import { Section, SectionHeading } from "@/components/ui/Section";
import { useSettings } from "@/content-context";
import { buildMetadata } from "@/lib/seo";
import { pageLoader } from "@/page-data";

/**
 * Transparency.
 *
 * Registration comes from the organisation profile, the documents from the
 * documents collection and the partners from the partner list; this page
 * decides where each appears. The footer links to #registration and #documents,
 * so both sections carry those anchors.
 */

export const loader = pageLoader("transparency");

export const meta = () =>
  buildMetadata({
    title: "Transparency",
    description:
      "Registration and affiliation for Himshikhara Socio-Cultural Society, and the honest status of every report, financial statement and policy.",
    path: "/transparency",
  });

function RegistrationSection({ section }: SectionProps) {
  const settings = useSettings();
  const link = linkSlot(section, 0);

  return (
    <Section id="registration">
      <div className="grid items-start gap-12 lg:grid-cols-2 lg:gap-16">
        <Reveal>
          <SectionHeading eyebrow={section.eyebrow} title={section.title || "Registration and affiliation"} />
          <dl className="mt-8 space-y-4">
            <div className="flex flex-wrap justify-between gap-x-6 gap-y-1 border-b border-line pb-4">
              <dt className="text-muted">Registered name</dt>
              <dd className="font-semibold text-ink">{settings.legalName}</dd>
            </div>
            {settings.establishedYear ? (
              <div className="flex flex-wrap justify-between gap-x-6 gap-y-1 border-b border-line pb-4">
                <dt className="text-muted">Established</dt>
                <dd className="font-semibold text-ink">{settings.establishedYear}</dd>
              </div>
            ) : null}
            {settings.registrations.map((reg) => (
              <div
                key={reg.value}
                className="flex flex-wrap justify-between gap-x-6 gap-y-1 border-b border-line pb-4 last:border-0"
              >
                <dt className="text-muted">{reg.label}</dt>
                <dd className="font-semibold text-ink">{reg.value}</dd>
              </div>
            ))}
          </dl>
        </Reveal>

        {section.texts?.panelTitle || section.body?.length ? (
          <Reveal>
            <div className="rounded-[16px] border border-line bg-mist p-6 md:p-8">
              {section.texts?.panelTitle ? <h2 style={{ fontSize: "var(--step-h3)" }}>{section.texts.panelTitle}</h2> : null}
              <Blocks blocks={section.body} className="mt-5" />
              {link ? (
                <div className="mt-7">
                  <ButtonLink href={link.href} variant="secondary">
                    {link.label}
                    <ArrowRight />
                  </ButtonLink>
                </div>
              ) : null}
            </div>
          </Reveal>
        ) : null}
      </div>
    </Section>
  );
}

function DocumentsSection({ section }: SectionProps) {
  const request = linkSlot(section, 0);
  return (
    <Section tone="ivory" ariaLabelledBy="documents-heading" id="documents" compact>
      <SectionHeading
        id="documents-heading"
        eyebrow={section.eyebrow}
        title={section.title || "Reports, financials and policies"}
        lead={section.lead}
      />

      <Reveal className="mt-10">
        <DocumentLibrary />
      </Reveal>

      {request ? (
        <Reveal className="mt-10">
          <ButtonLink href={request.href}>
            {request.label}
            <ArrowRight />
          </ButtonLink>
        </Reveal>
      ) : null}
    </Section>
  );
}

function PartnersSection() {
  return (
    <Section compact>
      <PartnerStrip />
    </Section>
  );
}

export const transparencySections: SectionRenderers = {
  "transparency.registration": RegistrationSection,
  "transparency.documents": DocumentsSection,
  "transparency.partners": PartnersSection,
};

export function TransparencyPage() {
  return (
    <>
      <PageHeader
        eyebrow="Accountability"
        title="Transparency"
        lead="The organisation's legal identity, and the documents available to funders and partners."
        trail={[
          { name: "Home", href: "/" },
          { name: "Transparency", href: "/transparency" },
        ]}
      />
      <PageSections renderers={transparencySections} />
    </>
  );
}
